import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import type { Customer, Tariff, TariffForm } from '../../types/finance';
import { TARIFF_FORMS } from '../../types/finance';
import type { TariffCreateDto } from '../../lib/api/endpoints';
import { parseMontoCOP, formatearCOP } from '../../utils/expenses';
import { parseCantidad, formatearCantidad, mensajeErrorFinanzas, hoyISODate } from '../../utils/finance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customers: Customer[];
  /**
   * Presente = «subir precios»: el formulario nace con los datos de esa
   * tarifa, con `validFrom` en hoy, y al guardar crea la vigencia nueva y
   * cierra la anterior el día antes. Los precios de la existente no se tocan.
   */
  base?: Tariff | null;
  onCrear: (data: TariffCreateDto, cerrarAnterior?: { id: string; validTo: string }) => Promise<unknown>;
}

interface FormState {
  customerId: string;
  name: string;
  form: TariffForm;
  unit: string;
  fixedAmount: string;
  unitPrice: string;
  discountPct: string;
  minimumAmount: string;
  minimumThreshold: string;
  vehicleClass: string;
  validFrom: string;
}

const vacio: FormState = { customerId: '', name: '', form: 'FIXED_PLUS_PER_UNIT', unit: 'kg', fixedAmount: '', unitPrice: '', discountPct: '0', minimumAmount: '', minimumThreshold: '', vehicleClass: '', validFrom: '' };

function diaAnterior(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * FE-32 — crear una tarifa, o crear la SIGUIENTE vigencia de una existente.
 *
 * No hay «editar precios» a propósito (ADR-030 §1): cada ingreso guarda la foto
 * de su tarifa, y reescribir los números dejaría la lista diciendo algo
 * distinto de lo que se cobró. Subir precios = otra fila con `validFrom` nuevo,
 * y la anterior se cierra el día anterior (`validTo` inclusivo).
 */
export function TariffFormModal({ isOpen, onClose, customers, base, onCrear }: Props) {
  const [form, setForm] = useState<FormState>(vacio);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (base) {
      setForm({
        customerId: base.customerId, name: base.name, form: base.form, unit: base.unit ?? 'kg',
        fixedAmount: base.fixedAmount ?? '', unitPrice: base.unitPrice ?? '', discountPct: String(base.discountPct),
        minimumAmount: base.minimumAmount ?? '', minimumThreshold: base.minimumThresholdQuantity ? String(Number(base.minimumThresholdQuantity)) : '',
        vehicleClass: base.vehicleClass ?? '', validFrom: hoyISODate(),
      });
      return;
    }
    setForm({ ...vacio, validFrom: hoyISODate() });
  }, [isOpen, base]);

  const set = (campo: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [campo]: e.target.value }));

  const usaFijo = form.form === 'FLAT' || form.form === 'FIXED_PLUS_PER_UNIT';
  const usaUnidad = form.form === 'PER_UNIT' || form.form === 'FIXED_PLUS_PER_UNIT';
  const fijo = form.fixedAmount.trim() ? parseMontoCOP(form.fixedAmount) : null;
  const minimo = form.minimumAmount.trim() ? parseMontoCOP(form.minimumAmount) : null;
  const precio = form.unitPrice.trim() ? parseCantidad(form.unitPrice) : null;
  const pct = form.discountPct.trim() === '' ? 0 : Number(form.discountPct.replace(',', '.'));
  const umbral = form.minimumThreshold.trim() ? parseCantidad(form.minimumThreshold) : null;

  const validar = (): string | null => {
    if (!form.customerId) return 'Elige el cliente.';
    if (!form.name.trim()) return 'Ponle nombre a la tarifa: la ruta, el destino o el servicio.';
    if (usaFijo && !fijo) return 'Indica el importe fijo.';
    if (usaUnidad && !form.unit.trim()) return 'Indica la unidad (kg, km, h…).';
    if (usaUnidad && !precio) return 'Indica el precio por unidad.';
    if (Number.isNaN(pct) || pct < 0 || pct > 100) return 'El descuento es un porcentaje entre 0 y 100.';
    if (form.minimumAmount.trim() && !minimo) return 'El mínimo no es un importe válido.';
    if (form.minimumThreshold.trim() && !umbral) return 'El umbral del mínimo no es una cantidad válida.';
    if (umbral && !minimo) return 'El umbral necesita un mínimo a cobrar.';
    if (umbral && !usaUnidad) return 'El umbral solo aplica a tarifas con cantidad (por unidad o fijo + por unidad).';
    if (!form.validFrom) return 'Indica desde cuándo rige.';
    if (base && form.validFrom <= base.validFrom) return `La nueva vigencia tiene que empezar después del ${base.validFrom}, cuando empezó la actual.`;
    return null;
  };

  const enviar = async () => {
    const problema = validar();
    if (problema) { setError(problema); return; }
    setEnviando(true);
    setError(null);
    try {
      await onCrear({
        customerId: form.customerId,
        name: form.name.trim(),
        form: form.form,
        unit: usaUnidad ? form.unit.trim() : undefined,
        fixedAmount: usaFijo ? fijo! : undefined,
        unitPrice: usaUnidad ? precio! : undefined,
        discountPct: usaUnidad ? pct : 0,
        minimumAmount: minimo ?? undefined,
        minimumThresholdQuantity: usaUnidad && umbral ? umbral : undefined,
        vehicleClass: form.vehicleClass.trim().toUpperCase() || undefined,
        validFrom: form.validFrom,
      }, base && !base.validTo ? { id: base.id, validTo: diaAnterior(form.validFrom) } : undefined);
      onClose();
    } catch (err) {
      setError(mensajeErrorFinanzas(err, 'tarifa'));
    } finally {
      setEnviando(false);
    }
  };

  const formaElegida = TARIFF_FORMS.find(f => f.id === form.form);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={base ? `Nueva vigencia — ${base.name}` : 'Nueva tarifa'} size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} isLoading={enviando}>{base ? 'Crear vigencia' : 'Crear tarifa'}</Button>
        </>
      }>
      <div className="form-stack">
        {base && (
          <p className="form-hint" style={{ padding: '0.75rem 0.875rem', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
            Los precios de una tarifa no se editan: cada ingreso guarda la foto de la suya. Esta vigencia
            nueva empieza el día que indiques y la actual se cierra el día anterior.
          </p>
        )}
        <Select label="Cliente" value={form.customerId} onChange={set('customerId')} disabled={!!base}
          options={[{ value: '', label: 'Selecciona el cliente…' }, ...customers.filter(c => c.active || c.id === form.customerId).map(c => ({ value: c.id, label: c.name }))]} />
        <Input label="Nombre (ruta, destino o servicio)" value={form.name} onChange={set('name')} placeholder="PDV Girón" />
        <Select label="Forma" value={form.form} onChange={set('form')} options={TARIFF_FORMS.map(f => ({ value: f.id, label: f.label }))} />
        {formaElegida && <p className="form-hint">{formaElegida.ayuda}</p>}

        <div className="form-pair">
          {usaFijo && (
            <div>
              <Input label="Importe fijo (COP)" value={form.fixedAmount} onChange={set('fixedAmount')} inputMode="decimal" placeholder="18.761" />
              <p className={`form-hint${form.fixedAmount.trim() && !fijo ? ' form-hint--error' : ''}`}>{fijo ? formatearCOP(fijo) : 'Por servicio o cargo base.'}</p>
            </div>
          )}
          {usaUnidad && (
            <div>
              <Input label="Unidad" value={form.unit} onChange={set('unit')} placeholder="kg" />
              <p className="form-hint">kg, km, h, viaje…</p>
            </div>
          )}
        </div>
        {usaUnidad && (
          <div className="form-pair">
            <div>
              <Input label={`Precio por ${form.unit || 'unidad'} (COP)`} value={form.unitPrice} onChange={set('unitPrice')} inputMode="decimal" placeholder="149" />
              <p className={`form-hint${form.unitPrice.trim() && !precio ? ' form-hint--error' : ''}`}>{precio ? `${formatearCOP(precio)} por ${form.unit || 'unidad'}` : 'Hasta cuatro decimales.'}</p>
            </div>
            <div>
              <Input label="Descuento sobre la cantidad (%)" value={form.discountPct} onChange={set('discountPct')} inputMode="decimal" placeholder="8" />
              <p className="form-hint">Se descuenta de la cantidad antes de multiplicar (las canastas: 8).</p>
            </div>
          </div>
        )}
        <div className="form-pair">
          <div>
            <Input label="Mínimo a cobrar (opcional)" value={form.minimumAmount} onChange={set('minimumAmount')} inputMode="decimal" placeholder="361.742" />
            <p className="form-hint">{minimo ? `Nunca menos de ${formatearCOP(minimo)}.` : 'El flete mínimo, si lo hay.'}</p>
          </div>
          {usaUnidad && (
            <div>
              <Input label={`Umbral del mínimo (${form.unit || 'unidad'}, opcional)`} value={form.minimumThreshold} onChange={set('minimumThreshold')} inputMode="decimal" placeholder="2.200" />
              <p className={`form-hint${form.minimumThreshold.trim() && !umbral ? ' form-hint--error' : ''}`}>
                {umbral ? `Por debajo de ${formatearCantidad(umbral, form.unit)} se cobra el mínimo fijo, sin fórmula.` : 'Vacío: el mínimo es solo piso.'}
              </p>
            </div>
          )}
        </div>
        <div className="form-pair">
          <div>
            <Input label="Clase de vehículo (opcional)" value={form.vehicleClass} onChange={set('vehicleClass')} placeholder="NHR" />
            <p className="form-hint">Vacío = aplica a todos.</p>
          </div>
        </div>
        <Input label="Rige desde" type="date" value={form.validFrom} onChange={set('validFrom')} />

        {error && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{error}</p>}
      </div>
    </Modal>
  );
}
